import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useComplaintsQuery } from '../../hooks/useComplaintsQuery';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { useDeleteEntitiesMutation, getUrlForOpenEntity } from '../../hooks/useEntityQuery';
import { useUserContext } from '../../context/user';
import { ComplaintResponse, getColorVariantForComplaintTopic, getComplaintFromString } from '../../types/api/complaints';
import Segment from '../navigation/Segment/Segment';
import { usersTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { useAlert } from '../../context/alert';
import TableActionsBanner from '../common/TableActionsBanner';
import { UserTableRow } from '../basic/UserTableRow';
import { pageSize } from '../../services/settings';
import { complaintsTableConfig } from './ComplaintsList+Table';
import { locale } from '../../helpers/strings';
import { isAllowAction } from '../../services/permissions/utils';
import { DisplayComplaintModal } from './components/DisplayComplaintModal';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { renderTruncatedText } from '../../helpers/strings';
import { formatDateTime } from '../../helpers/dates';
import { FeedbackStatus, getFeedbackStatusColor, getFeedbackStatusLabel, statusFilterConfig } from '../../types/api/feedback';
import { Stack, Checkbox, Button, Label, Spinner, ActionMenu, ActionList, IconButton } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { XCircleIcon, TrashIcon, FilterIcon, BriefcaseIcon } from '@primer/octicons-react'

const EMPTY_SET = new Set<string>();

export default function ComplaintsList() {
    type complaintAction = 'block';

    const { profile } = useUserContext();
    const navigate = useNavigate();

    const [pageIndex, setPageIndex] = React.useState(1)
    const [selectedStatus, setSelectedStatus] = useState<FeedbackStatus | undefined>(undefined);

    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const [selectedComplaint, setSelectedComplaint] = useState<ComplaintResponse | undefined>(undefined);
    const [openComplaintModal, setOpenComplaintModal] = React.useState(false)
    const [currentRouteEntityId, setCurrentRouteEntityId] = useState<string | undefined>(undefined);

    const { setSuccess, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const {
        isLoading: isLoadingList,
        data,
        refetch,
        isFetched,
        isError
    } = useComplaintsQuery(
        "",
        pageIndex,
        pageSize,
        "-created_at",
        selectedStatus
    );

    const { mutate: deleteEntities, isPending } = useDeleteEntitiesMutation();

    const handleDeleteComplaints = (complaints: ComplaintResponse[]) => {
        deleteEntities(complaints, {
            onSuccess: (data) => {
                dismissTableActionAlert()
                setSuccess(complaints.length === 1 ? `Объект жалобы заблокирован` : `Объекты жалобы заблокированы`);
                if (data.failed > 0) {
                    setError(`Ошибок: ${data.failed}`);
                }
            }
        });
    };

    const isLoading = isLoadingList || isPending;

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);

    const emptyListDescription = `В сервисе ещё нет жалоб
    от пользователей`;

    // ACTIONS
    const useComplaintAction = (action: complaintAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'block':
                        const complaints = data ? data.list.filter((item) => ids.includes(item.id)) : [];
                        if (complaints.length > 0) {
                            handleDeleteComplaints(complaints)
                        }
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const blockSelectedComplaintsObjects = useComplaintAction('block', actionsAlertId);

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined
        props = createAlertPropsForSended(count)

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    // TABLE ACTIONS ALERT
    function createAlertPropsForSended(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);

        const buttons = [
            <Button key="clear-button"
                variant="danger"
                disabled={isLoading}
                leadingVisual={TrashIcon}
                onClick={showConfirmBlockAlert}>
                Заблокировать объект жалобы
            </Button>
        ]

        return {
            count: count,
            title: title,
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createConfirmBlockAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите заблокировать?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" disabled={isLoading} onClick={blockSelectedComplaintsObjects}>
                    Да
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showConfirmBlockAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmBlockAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {
        if (data) {
            if (isSelected) {
                setSelectedRows(new Set(data.list.map(row => row.id)));
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const totalRows = data.list.length;
            const selectedFromCurrentData = data.list.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {

        if (selectedRowsRef.current.size === 0) {
            rowSelect(rowId, isSelected)
        } else {
            rowSelect(rowId, isSelected)
        }
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    const complaintDismissed = () => {
        setSelectedComplaint(undefined)
        setOpenComplaintModal(false)
    }

    const complaintObjectBlocked = () => {
        setSelectedComplaint(undefined)
        setOpenComplaintModal(false)
        refetch()
    }

    const openModal = (complaint: ComplaintResponse) => {
        setSelectedComplaint(complaint)
        setOpenComplaintModal(true)
    }

    const openComplaintEntity = async (complaint: ComplaintResponse) => {
        setCurrentRouteEntityId(complaint.id)
        try {
            const url = await getUrlForOpenEntity(complaint.targetEntityType, complaint.targetEntityId)
            if (url) {
                //window.open(url, '_blank')
                navigate(url)
            }
        } catch (error) {
            console.error('Error opening entity:', error)
            setError('Не удается получить объект жалобы')
        } finally {
            setCurrentRouteEntityId(undefined)
        }
    }

    const complaintsStatusChanged = () => {
        refetch()
    }

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Segment items={usersTabsData(profile)} />
                    <Stack direction="horizontal">
                        <ActionMenu>
                            <ActionMenu.Anchor>
                                <IconButton icon={FilterIcon} aria-label="Фильтр" />
                            </ActionMenu.Anchor>
                            <ActionMenu.Overlay>
                                <ActionList
                                    selectionVariant="single"
                                    role="menu"
                                    aria-label="Item"
                                    className='w-[232px]'
                                >
                                    <ActionList.Item
                                        key="all"
                                        role="menuitemradio"
                                        selected={selectedStatus === undefined}
                                        aria-checked={selectedStatus === undefined}
                                        onSelect={() => {
                                            setSelectedStatus(undefined)
                                        }}
                                    >
                                        <Stack align="center" direction="horizontal" gap='condensed'>
                                            <BriefcaseIcon size={16} />
                                            Все обращения
                                        </Stack>
                                    </ActionList.Item>
                                    <ActionList.Divider className='block mt-2' />
                                    {Object.entries(statusFilterConfig).map(([status, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <ActionList.Item
                                                key={status}
                                                role="menuitemradio"
                                                selected={selectedStatus === status}
                                                aria-checked={selectedStatus === status}
                                                onSelect={() => setSelectedStatus(status as FeedbackStatus)}
                                            >
                                                <Stack align="center" direction="horizontal" gap='condensed'>
                                                    <Icon size={16} />
                                                    {config.label}
                                                </Stack>
                                            </ActionList.Item>
                                        );
                                    })}
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {openComplaintModal && selectedComplaint && (<DisplayComplaintModal
                        isOpen={openComplaintModal}
                        complaint={selectedComplaint}
                        onDidBlock={complaintObjectBlocked}
                        onDidComplaintStatusChanged={complaintsStatusChanged}
                        onClose={complaintDismissed}
                    />)}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="complaints-loading"
                            rows={pageSize}
                            columns={complaintsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Жалобы не найдены'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="complaints-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (complaint: ComplaintResponse) => {
                                            return (
                                                <Checkbox
                                                    checked={selectedRows.has(complaint.id)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleRowSelect(complaint.id, e.target.checked)
                                                    }
                                                    aria-label={`Выделить ${complaint.id}`}
                                                />
                                            )
                                        }
                                    },
                                    {
                                        id: 'name',
                                        rowHeader: true,
                                        header: "Пользователь",
                                        renderCell: (row) => {
                                            const user = (row as ComplaintResponse).user;
                                            return <UserTableRow
                                                user={user}
                                                onClick={() => navigate('/users/user?id=' + user.id)} />
                                        }
                                    },
                                    {
                                        id: 'phone',
                                        header: "Номер телефона",
                                        renderCell: (row) => {
                                            const complaint = row as ComplaintResponse;
                                            return (complaint.user?.username === 'Unknown User' && complaint.user.phone == null) ? '-' : complaint.user.phone
                                        }
                                    },
                                    {
                                        id: 'email',
                                        header: "E-mail",
                                        renderCell: (row) => {
                                            const complaint = row as ComplaintResponse;
                                            return complaint.user.email
                                        }
                                    },
                                    {
                                        id: 'type',
                                        header: "Тип жалобы",
                                        renderCell: (row) => {
                                            const topicModel = (row as ComplaintResponse).targetEntityType;
                                            let complaintTopic = getComplaintFromString(topicModel)
                                            let variant = getColorVariantForComplaintTopic(topicModel ?? "system")
                                            return <Label variant={variant}>{complaintTopic}</Label>
                                        }
                                    },
                                    {
                                        id: 'text',
                                        header: 'Комментарий',
                                        renderCell: (row) => {
                                            const complaint = (row as ComplaintResponse);
                                            return (
                                                <Button
                                                    onClick={() => openModal(complaint)}
                                                    size="small"
                                                    variant='invisible'
                                                    className='block text-[#0969da]'
                                                    aria-label="Открыть">{renderTruncatedText(complaint.text, 32)}
                                                </Button>
                                            )
                                        }
                                    },
                                    {
                                        id: 'object',
                                        header: 'Объект жалобы',
                                        renderCell: (row) => {
                                            const complaint = (row as ComplaintResponse);
                                            const isAllowView = isAllowAction(profile, 'VIEW_USER_COMPLAINTS')
                                            if (isAllowView) {
                                                if (currentRouteEntityId === complaint.id) {
                                                    return <Spinner />
                                                } else {
                                                    return <button
                                                        onClick={() => openComplaintEntity(complaint)}
                                                        className='text-[#0969da] text-[14px] font-normal underline'
                                                        aria-label="Открыть">Перейти к источнику
                                                    </button>
                                                }
                                            }
                                            return <></>
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: 'Дата и время',
                                        renderCell: (row) => {
                                            const date = (row as ComplaintResponse).createdAt;
                                            return formatDateTime(date, ',')
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: 'Статус',
                                        renderCell: (row) => {
                                            const status = (row as ComplaintResponse).status;
                                            const color = getFeedbackStatusColor(status)
                                            return <p style={{ color: color }}>{getFeedbackStatusLabel(status)}</p>
                                        }
                                    },
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}